import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import {
  Box, Typography, Button, Paper, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Select, FormControl, InputLabel,
  Alert, Tooltip, Skeleton, Tabs, Tab,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import { useAuth, DEFAULT_PARTNER_CONTEXT } from '../../auth/AuthContext';
import ApiErrorAlert from '../../components/ApiErrorAlert';
import { listKeys, createKey, revokeKey } from '../../api/developerApi';
import ScopedKeysList from './ScopedKeysList';
import ScopedKeyCreateDialog from './ScopedKeyCreateDialog';

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy'}>
      <IconButton size="small" aria-label="Copy" onClick={handleCopy}><ContentCopyIcon fontSize="small" /></IconButton>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// Standard Keys tab — the existing sk_* CRUD UX, lifted into its own
// component so the page-level <Tabs> can mount + unmount it without
// disturbing the new Scoped Keys tab (and vice versa).
//
// Behavior is intentionally identical to the pre-tab implementation.
// Don't add new fields, columns, or interactions here — those belong in
// the parent page or in <ScopedKeysTab>. This component stays the
// reference shape for the sk_* lifecycle.
// ---------------------------------------------------------------------------

function StandardKeysTab() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [env, setEnv] = useState('live');
  // Status filter — revoked keys accumulate and bury the active ones, so we
  // default to 'active'. 'all'/'revoked' stay available for auditing.
  const [statusFilter, setStatusFilter] = useState('active');
  const [createOpen, setCreateOpen] = useState(false);
  const [newKey, setNewKey] = useState(null); // shown once after creation
  const { register, handleSubmit, reset } = useForm();

  const tenantId = env === 'live' ? user?.liveTenantId : user?.testTenantId;

  const { data: keys, isLoading } = useQuery(
    ['keys', tenantId], () => listKeys(tenantId), { enabled: !!tenantId }
  );

  // Backend stores status lowercase ('active' / 'revoked' — DeveloperKeyHandler);
  // compare case-insensitively to also cover any legacy uppercase rows. Anything
  // not active is treated as revoked (matches the Status chip's color rule below).
  const isActive = (key) => (key.status || '').toLowerCase() === 'active';
  const allKeys = keys || [];
  // Defensive env scoping: GET /developer/keys currently returns the partner's
  // live AND test keys regardless of the tenantId we pass, so the selected
  // environment wouldn't otherwise narrow the list. Scope to the active env's
  // tenant here — mirrors ScopedKeysList. (Honoring the tenantId server-side is
  // a tracked follow-up; every key row carries its own tenantId.)
  const envKeys = allKeys.filter((key) => key.tenantId === tenantId);
  const visibleKeys = envKeys.filter((key) => {
    if (statusFilter === 'active') return isActive(key);
    if (statusFilter === 'revoked') return !isActive(key);
    return true;
  });

  const createMutation = useMutation(createKey, {
    onSuccess: (data) => {
      // Guard a malformed/empty body — a bare `data.rawKey` would throw inside
      // onSuccess and leave the dialog wedged (sibling of #98). Null just means
      // the one-time key view is skipped; the list still refetches.
      setNewKey(data?.rawKey ?? null); // shown once
      qc.invalidateQueries(['keys', tenantId]);
      reset();
    },
  });

  const revokeMutation = useMutation(revokeKey, {
    onSuccess: () => qc.invalidateQueries(['keys', tenantId]),
  });

  const onSubmit = ({ keyName }) => {
    createMutation.mutate({ keyName, tenantId });
  };

  const handleCloseCreate = () => {
    setCreateOpen(false);
    setNewKey(null);
    reset();
    createMutation.reset(); // clear any prior error so it doesn't flash on reopen
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="body2" color="text.secondary">Long-lived secret keys (<code>sk_live_*</code> / <code>sk_test_*</code>) for your server-side integrations</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Environment</InputLabel>
            <Select value={env} label="Environment" onChange={(e) => setEnv(e.target.value)}>
              <MenuItem value="live">Live</MenuItem>
              <MenuItem value="test">Test</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="revoked">Revoked</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}
            sx={{ bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' }, textTransform: 'none', fontWeight: 600 }}>
            New key
          </Button>
        </Box>
      </Box>

      <ApiErrorAlert
        error={revokeMutation.error}
        onClose={() => revokeMutation.reset()}
        sx={{ mb: 2 }}
      />

      <Paper elevation={0} sx={{ border: '1px solid', borderRadius: 2, overflowX: 'auto' }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: 'background.default' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Key ID</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Created</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 5 }).map((_, j) => (
                    <TableCell key={j}><Skeleton /></TableCell>
                  ))}
                </TableRow>
              ))
              : visibleKeys.map((key) => (
                <TableRow key={key.keyId} hover>
                  <TableCell sx={{ fontSize: 13 }}>{key.keyName}</TableCell>
                  <TableCell sx={{ fontSize: 12, fontFamily: 'monospace', color: '#64748b' }}>{key.keyId}</TableCell>
                  <TableCell>
                    <Chip label={key.status} size="small"
                      sx={{
                        bgcolor: isActive(key) ? '#dcfce7' : '#fee2e2',
                        color: isActive(key) ? '#166534' : '#991b1b',
                        fontWeight: 600, fontSize: 11,
                      }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: '#64748b' }}>
                    {new Date(key.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Revoke">
                      {/* Disable only the in-flight row's button so a double-click
                          can't fire revokeKey twice (other rows stay usable). */}
                      <span>
                        <IconButton size="small" color="error"
                          aria-label="Revoke key"
                          disabled={revokeMutation.isLoading && revokeMutation.variables === key.keyId}
                          onClick={() => revokeMutation.mutate(key.keyId)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            {!isLoading && visibleKeys.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: '#94a3b8', fontSize: 13 }}>
                  {allKeys.length === 0
                    ? 'No keys yet. Create your first key to get started.'
                    : `No ${statusFilter === 'all' ? '' : `${statusFilter} `}keys to show.`}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={handleCloseCreate} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Create API key</DialogTitle>
        <DialogContent>
          {newKey ? (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Copy this key now — it won't be shown again.
              </Alert>
              <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'background.default', border: '1px solid', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flexGrow: 1, fontSize: 12 }}>
                  {newKey}
                </Typography>
                <CopyButton text={newKey} />
              </Paper>
            </Box>
          ) : (
            <Box component="form" id="create-key-form" onSubmit={handleSubmit(onSubmit)}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <ApiErrorAlert error={createMutation.error} />
              <TextField label="Key name" size="small" fullWidth
                placeholder="e.g. Production backend"
                {...register('keyName', { required: true })} />
              <Typography variant="caption" color="text.secondary">
                Environment: <strong>{env}</strong> ({tenantId})
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {newKey
            ? <Button onClick={handleCloseCreate} variant="contained"
                sx={{ bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' }, textTransform: 'none' }}>
                Done
              </Button>
            : <>
                <Button onClick={handleCloseCreate} sx={{ textTransform: 'none' }}>Cancel</Button>
                <Button type="submit" form="create-key-form" variant="contained" disabled={createMutation.isLoading}
                  sx={{ bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' }, textTransform: 'none', fontWeight: 600 }}>
                  {createMutation.isLoading ? 'Creating…' : 'Create'}
                </Button>
              </>
          }
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Scoped Keys tab — list + revoke (Step 3) + 5-step create wizard (Step 4).
// ---------------------------------------------------------------------------

function ScopedKeysTab() {
  const [createOpen, setCreateOpen] = useState(false);
  // env + context are owned here (not inside ScopedKeysList) so the create
  // wizard's onCreated handoff can switch the list to a freshly-minted key's
  // (env, context) — closing the create→list→revoke loop (#387). Defaults to
  // the reserved vectros-admin control-plane context (no regression).
  const [env, setEnv] = useState('live');
  const [context, setContext] = useState(DEFAULT_PARTNER_CONTEXT);
  return (
    <>
      <ScopedKeysList
        onCreateClick={() => setCreateOpen(true)}
        env={env}
        onEnvChange={setEnv}
        context={context}
        onContextChange={setContext}
      />
      <ScopedKeyCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        initialEnv={env}
        onCreated={(createdEnv, createdContext) => {
          // Switch the list to the freshly-minted key's (env, context) so it's
          // immediately visible — closes create→list→revoke (#387). This changes
          // `env` (→ the dialog's initialEnv) while the dialog is still open on its
          // confirmation step; that is SAFE because the dialog only resets on the
          // open-transition, not on an initialEnv change (see its reset effect).
          setEnv(createdEnv);
          setContext(createdContext);
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Tab routing
//
// URL fragment ↔ tab state: `/keys#scoped` lands on the Scoped tab;
// switching tabs replaces the fragment so bookmarks + browser-back
// behave naturally without remounting the page.
//
// Always-mounted panel pattern: both <StandardKeysTab/> and
// <ScopedKeysTab/> stay mounted always, hidden via `display: none` on
// the inactive one. This preserves in-flight dialog + form state across
// tab switches (acceptance criterion from MR #3 §4 Step 2). Cost is
// minimal — both panels' react-query subscriptions are cheap and would
// have fired on the next visit anyway.
// ---------------------------------------------------------------------------

const TAB_STANDARD = 'standard';
const TAB_SCOPED = 'scoped';

function getInitialTab() {
  if (typeof window === 'undefined') return TAB_STANDARD;
  const hash = window.location.hash.replace('#', '');
  return hash === TAB_SCOPED ? TAB_SCOPED : TAB_STANDARD;
}

export default function KeysPage() {
  const [tab, setTab] = useState(getInitialTab);

  // Keep URL fragment in sync with active tab so bookmarks work.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const current = window.location.hash.replace('#', '');
    if (current !== tab) {
      const url = new URL(window.location.href);
      url.hash = tab === TAB_STANDARD ? '' : tab;
      window.history.replaceState(null, '', url.toString());
    }
  }, [tab]);

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>API Keys</Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          aria-label="API key types"
          sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}
        >
          <Tab label="Standard Keys" value={TAB_STANDARD} />
          <Tab label="Scoped Keys" value={TAB_SCOPED} />
        </Tabs>
      </Box>

      <Box sx={{ display: tab === TAB_STANDARD ? 'block' : 'none' }}>
        <StandardKeysTab />
      </Box>
      <Box sx={{ display: tab === TAB_SCOPED ? 'block' : 'none' }}>
        <ScopedKeysTab />
      </Box>
    </Box>
  );
}
